import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useAuth } from '@App/components/AuthProvider/auth-provider.context';

import { faArrowsMinimize, faBus, faUsers } from '@fortawesome/pro-solid-svg-icons';

import Nav from '@App/components/Nav/Nav';
import Footer from '@App/components/Footer/Footer';
import Partners from '@App/components/Partners/Partners';
import RegisterForm from '@App/components/RegisterForm/RegisterForm';
import { useLoginModal } from '@services/hooks.service';
import { setShowContactModal } from '@App/app.slice';

import {
  Button, Icon, IconList, 
} from '@nazka/nazka.mapframe.components';

import './register-employer.styl';

function RegisterEmployer() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { setShowLoginModal } = useLoginModal();

  const {
    isLoggedIn,
  } = useAuth();

  const scrollToRegister = useCallback(() => {
    document.querySelector('.register-employer__register h3').scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (searchParams.get('scrollToRegister')) {
      searchParams.delete('scrollToRegister');
      setSearchParams(searchParams, { replace: true });
      scrollToRegister();
    }
  }, [searchParams, setSearchParams, scrollToRegister]);

  return (
    <div className="register-employer">
      <Nav />
      <div className="register-employer__intro">
        <h1 className="register-employer__intro__title">
          Zet je onderneming op de kaart
        </h1>
        <h3 className="register-employer__intro__subtitle">
          Welke troeven heeft je onderneming qua mobiliteit en bereikbaarheid? 
          Een station vlakbij of een overdekte fietsenparking?
        </h3>
        <div className="register-employer__intro__buttons">
          {!isLoggedIn && (
            <Button 
              className="register-employer__intro__buttons__button"
              onClick={() => {
                setShowLoginModal('werkgever');
                window.gtag?.('event', 'home_employer_click_intro_login', {
                  event_category: 'Account',
                  event_label: 'Home employer click into login button',
                });
              }}
            >
              Inloggen
            </Button>
          )}
          <Button 
            className="register-employer__intro__buttons__button"
            onClick={() => {
              scrollToRegister();
              window.gtag?.('event', 'home_employer_click_intro_register', {
                event_category: 'Account',
                event_label: 'Home employer click into register button',
              });
            }}
          >
            Registreren
          </Button>
        </div>
      </div>
      <div className="register-employer__info">
        <div className="register-employer__info__image">
          <Icon 
            className="register-employer__info__image__icon"
            icon="/img/icons/mobiliteitsaanbod.svg"
          />
        </div>
        <div className="register-employer__info__text">
          <h3>Nieuw talent voor je bedrijf is dichterbij dan je denkt</h3>
          <p>
            Op NaarJobs vinden mensen hun ideale job op basis van bereikbaarheid. 
            Biedt jouw bedrijf of organisatie fietsleasing, een treinabonnement of 
            andere mobiliteitsvoordelen aan? Met NaarJobs zet je die extra’s extra 
            in de verf en trek jij nieuw talent aan. Steeds meer mensen zoeken trouwens 
            een job in de buurt, waardoor de bereikbaarheid van jouw organisatie een 
            belangrijke plus is.
          </p>
          <p>De troeven van NaarJobs:</p>
          <IconList
            className="register-employer__info__text__list"
            items={[
              {
                key: 0, 
                content: 'Jouw vacatures komen automatisch op het platform',
                iconProps: { icon: faArrowsMinimize }, 
              },
              {
                key: 1, 
                content: 'Toon de ligging van je bedrijf en de mobiliteitsopties in de buurt',
                iconProps: { icon: faBus }, 
              },
              {
                key: 2, 
                content: 'Supersnel en gratis registreren om de mobiliteitsvoordelen van jouw organisatie in de verf te zetten',
                iconProps: { icon: '/img/icons/euro-sign-solid-strike-through.svg' }, 
              },
              {
                key: 3, 
                content: 'Een extra, innovatief kanaal om nieuw talent te rekruteren',
                iconProps: { icon: faUsers }, 
              },
            ]}
          />
          <p>Vergroot je zichtbaarheid, zet de mobiliteitsvoordelen van jouw organisatie op NaarJobs en maak die match!</p>
        </div>
      </div>
      <div className="register-employer__register">
        <div className="register-employer__register__form">
          <h3>Registreren</h3>
          <RegisterForm setShowLoginModal={() => setShowLoginModal('werkgever')} group="employers" />
        </div>
        <div className="register-employer__register__image">
          <Icon className="register-employer__register__image__icon" icon="/img/illustrations/job-offer.svg" />
        </div>
      </div>
      <div className="register-employer__content-block">
        <div className="register-employer__content-block__wrapper">
          <div className="register-employer__content-block__wrapper__info">
            <div className="register-employer__content-block__wrapper__info__image">
              <Icon
                className="register-employer__content-block__wrapper__info__image__icon" 
                icon="/img/illustrations/email-red.svg"
              />
            </div>
            <div className="register-employer__content-block__wrapper__info__text">
              <h3>Contact</h3>
              <p>
                Zit je met een vraag of wil je graag met ons samenwerken? 
                {' Stuur een e-mail naar '}
                <a href="mailto:info@naarjobs.be">
                  info@naarjobs.be
                </a>
                {' of gebruik het contactformulier.'}
              </p>
              <Button
                className="register-employer__content-block__wrapper__info__text__button"
                onClick={() => {
                  dispatch(setShowContactModal(true));
                }}
              >
                Contact
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Partners />
      <Footer />
    </div>
  );
}

export default RegisterEmployer;
